import React, { useState, useEffect } from 'react';
import { FaLink } from 'react-icons/fa';
import { useGetUserQuery } from '../../../redux/auth';
import { useUpdateFirstMessageMutation } from '../../../redux/messageView';
import SwipeCardCarousel from './SwipeCard';
import PlayScreenSkeleton from './SkelentonLoader';
import DefaultProfileImage from '../../../../assets/ProfileImage.png';
import { enhancedShare } from '../../../utils/webviewDetector';

interface PlayProps {
  handleOpenShareDrawer: () => void;
  username?: string;
  activeCardIndex: number;
  setActiveCardIndex: React.Dispatch<React.SetStateAction<number>>;
  onMount?: () => void;
  onProfileImageChange?: (image: string) => void;
  onShareLinkChange?: (link: string) => void;
} 

const Play: React.FC<PlayProps> = ({
  handleOpenShareDrawer,
  username,
  activeCardIndex,
  setActiveCardIndex,
  onMount,
  onProfileImageChange,
  onShareLinkChange,
}) => {
  const [copied, setCopied] = useState(false);
  const [profileImage, setProfileImage] = useState<string>(DefaultProfileImage);
  const [originalFirstMessage, setOriginalFirstMessage] = useState<string>('');
  const [currentFirstMessage, setCurrentFirstMessage] = useState<string>('');

  const { data: userData, isSuccess, isError, isLoading } = useGetUserQuery();
  const [updateFirstMessage] = useUpdateFirstMessageMutation();

  // Set profile image with fallback to localStorage
  useEffect(() => {
    const profilePic = userData?.data?.profile_picture;

    if (isSuccess && profilePic) {
      setProfileImage(profilePic);
      localStorage.setItem('profileImage', profilePic);
      onProfileImageChange?.(profilePic);
    } else if (isError || !profilePic) {
      const storedImage = localStorage.getItem('profileImage');
      if (storedImage && !storedImage.startsWith('blob:')) {
        setProfileImage(storedImage);
        onProfileImageChange?.(storedImage);
      }
    }
  }, [isSuccess, isError, userData, onProfileImageChange]);

  // Call onMount when component is ready
  useEffect(() => {
    if (onMount && !isLoading) {
      onMount();
    }
  }, [onMount, isLoading]);

  const swipeMessages = userData?.swipeMessages || [];

  // Store original first message when data loads
  useEffect(() => {
    if (swipeMessages.length > 0 && !originalFirstMessage) {
      const firstMessageContent = swipeMessages[0]?.content || '';
      setOriginalFirstMessage(firstMessageContent);
      setCurrentFirstMessage(firstMessageContent);
    }
  }, [swipeMessages, originalFirstMessage]);

  const messageId = swipeMessages[activeCardIndex]?.slug;
  const shareLink = messageId ? `brochat.chat/${messageId}` : 'brochat.chat';
  
  // Update share link in parent component
  useEffect(() => {
    onShareLinkChange?.(shareLink);
  }, [shareLink, onShareLinkChange]);

  // Show skeleton loader only when API is loading
  if (isLoading) {
    return <PlayScreenSkeleton />;
  }

  // Optional: show message if no cards available
  if (swipeMessages.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen text-center text-gray-600">
        No messages found. Share your link to get started!
      </div>
    );
  }

  const currentMessage = swipeMessages[activeCardIndex];
  const isFirstCard = activeCardIndex === 0;

  const handleCopy = async () => {
    try {
      // Try enhanced sharing first
      await enhancedShare({
        title: 'Check out my anonymous Q&A!',
        text: `Send me anonymous messages at https://${shareLink}`,
        url: `https://${shareLink}`
      });
      setCopied(true);
    } catch (error) {
      console.error('Enhanced share failed, falling back to clipboard:', error);
      // Fallback to clipboard
      await navigator.clipboard.writeText(`https://${shareLink}`);
      setCopied(true);
    }
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFirstCardUpdate = async (newContent: string) => {
    try {
      const firstMessage = swipeMessages[0];
      if (!firstMessage?.id) {
        console.error("No message ID found for first message");
        return;
      }
      await updateFirstMessage({ content: newContent, messageId: firstMessage.id.toString() }).unwrap();
      setCurrentFirstMessage(newContent);
      console.log("First message updated successfully");
    } catch (error) {
      console.error("Failed to update first message:", error);
    }
  };

  const handleFirstCardReset = async () => {
    try {
      const firstMessage = swipeMessages[0];
      if (!firstMessage?.id) {
        console.error("No message ID found for first message");
        return;
      }
      await updateFirstMessage({ content: originalFirstMessage, messageId: firstMessage.id.toString() }).unwrap();
      setCurrentFirstMessage(originalFirstMessage);
      console.log("First message reset to original");
    } catch (error) {
      console.error("Failed to reset first message:", error);
    }
  };

  return (
    <div className="flex flex-col justify-between">
      <div className="h-10" />

      {/* Swipe Cards */}
      <SwipeCardCarousel
        profileImage={profileImage}
        onIndexChange={setActiveCardIndex}
        swipeMessages={swipeMessages}
        onFirstCardUpdate={handleFirstCardUpdate}
        onFirstCardReset={handleFirstCardReset}
        originalFirstMessage={originalFirstMessage}
        currentFirstMessage={currentFirstMessage}
      />

      <div className="h-3" />

      {/* Steps */}
      <div className="w-full mt-8 space-y-4 px-2 max-w-md mx-auto">
        <div className="rounded-2xl bg-gray-200/60 p-4 text-center">
          <p className="font-semibold text-base md:text-lg text-black/80 mb-2">
            Step 1: Copy your link
          </p>
          <p className="text-gray-400 font-semibold mb-2">{shareLink}</p>
          <div className="flex justify-center">
            <div
              onClick={handleCopy}
              className="rounded-full border-2 border-red-500 py-1 px-4 text-red-500 font-bold flex items-center gap-1 cursor-pointer"
            >
              <FaLink />
              {copied ? 'copied!' : 'copy link'}
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-gray-200/60 p-4 text-center">
          <p className="font-semibold text-base text-black/80 md:text-lg mb-3">
            Step 2: Share link on your story
          </p>
          <button
            onClick={() => {
              console.log("Share button clicked!");
              handleOpenShareDrawer();
            }}
            style={{
              background: isFirstCard
                ? '#EF4444'
                : `linear-gradient(to right, ${currentMessage?.bgStart || '#EF4444'}, ${currentMessage?.bgEnd || '#F472B6'})`,
            }}
            className="rounded-full px-8 py-3 text-white text-lg font-bold transition-all duration-300"
          >
            Share Your Link!
          </button>
        </div>
      </div>
    </div>
  );
};

export default Play;